import React, { useState, useRef, useEffect } from 'react';
import { Info, X, ChevronDown, Search, Menu, Trash2, ChevronsUpDown } from 'lucide-react';
import { useHistory, useLocation } from "react-router-dom";
import Tooltip from '../addentities/Tooltip.js';
import { ReactSortable } from 'react-sortablejs';
import { createPortal } from 'react-dom';
import WorkInProgress from '../../components/WorkInProgress.js';
import TestSyncModal from '../../components/TestSyncModal.js';
import CharacterLimitedTextarea from '../../components/CharacterLimitedTextarea';
import './style.css';
import apiHelper from '../../apiHelper.js';
import EmpltyBoxIcon from '../../assets/img/brand/box.png';
import AlertNotification from '../../components/AlertNotification.js';
import { pl } from 'date-fns/locale';
import UnsavedChangesPrompt from '../../components/UnsavedChangesPrompt.js';

const portalRoot = typeof document !== 'undefined' ? document.getElementById('portal-root') : null;
const OptionPortal = ({ children, style }) => {
    if (portalRoot) {
        return createPortal(
            <div className="options-container w-full bg-white border rounded shadow-lg" style={{ ...style, overflow: 'auto', zIndex: 2000 }}>
                {children}
            </div>,
            portalRoot
        );
    }

    return (
        <div className="options-container w-full bg-white border rounded shadow-lg" style={{ ...style, overflow: 'auto', zIndex: 2000 }}>
            {children}
        </div>
    );
};

// Success SVG icon component (using your exact SVG)
const SuccessIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ marginRight: '4px' }}>
        <path d="M7.99967 1.3335C4.32367 1.3335 1.33301 4.32416 1.33301 8.00016C1.33301 11.6762 4.32367 14.6668 7.99967 14.6668C11.6757 14.6668 14.6663 11.6762 14.6663 8.00016C14.6663 4.32416 11.6757 1.3335 7.99967 1.3335ZM7.11631 10.4929C6.86809 10.7411 6.46571 10.7413 6.21722 10.4933L4.66331 8.94278C4.40281 8.68284 4.40228 8.26096 4.66213 8.00037C4.92215 7.73962 5.34435 7.73914 5.60496 7.99929L6.66567 9.05816L9.72367 6.00016C9.98398 5.73985 10.406 5.73985 10.6663 6.00016C10.9267 6.26047 10.9267 6.68252 10.6663 6.94283L7.11631 10.4929Z" fill="#146C43" />
    </svg>
);

// Failed SVG icon component (using your exact SVG)
const FailedIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ marginRight: '4px' }}>
        <path d="M7.96834 1.3335C4.30967 1.3335 1.33301 4.32416 1.33301 8.00016C1.33301 11.6762 4.32367 14.6668 7.99967 14.6668C11.6757 14.6668 14.6663 11.6762 14.6663 8.00016C14.6663 4.32416 11.6617 1.3335 7.96834 1.3335ZM8.66634 10.6668C8.66634 11.035 8.36786 11.3335 7.99967 11.3335C7.63148 11.3335 7.33301 11.035 7.33301 10.6668C7.33301 10.2986 7.63148 10.0002 7.99967 10.0002C8.36786 10.0002 8.66634 10.2986 8.66634 10.6668ZM8.66634 8.00016C8.66634 8.36835 8.36786 8.66683 7.99967 8.66683C7.63148 8.66683 7.33301 8.36835 7.33301 8.00016V5.3335C7.33301 4.96531 7.63148 4.66683 7.99967 4.66683C8.36786 4.66683 8.66634 4.96531 8.66634 5.3335V8.00016Z" fill="#B02A37" />
    </svg>
);

export default function AddListing() {
    const history = useHistory();

    // Discard modal
    const [discard_modal_open, set_discard_modal_open] = useState(false);
    const [stockOptions, setStockOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const discard_ref = useRef(null);
    const [successMessage, setSuccessMessage] = useState('');
    const handle_discard = () => set_discard_modal_open(true);
    useEffect(() => {
        function handleClickOutside(event) {
            if (discard_ref.current && !discard_ref.current.contains(event.target)) {
                set_discard_modal_open(false);
            }
        }
        if (discard_modal_open) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [discard_modal_open]);

    const breadcrumbs = ["Listing", "Platform Listing", "Add Listing"];
    const [selectStockError, setSelectStockError] = useState("");
    const [isDirty, setIsDirty] = useState(false);

    // Platform mapping rows
    const [rows, set_rows] = useState([]);
    const [rowsUpdateCheck, setRowsUpdateCheck] = useState(0);
    const [enableTestSync, setEnableTestSync] = useState(false);
    const [showTestSyncModal, setShowTestSyncModal] = useState(false);
    const row_refs = useRef({});
    const location = useLocation();
    const [duplicate_stock_error, set_duplicate_stock_error] = useState(false);

    // Alert state
    const [showItemAlert, setShowItemAlert] = useState(false);
    const [alert, setAlert] = useState({
        type: "",
        action: "",
        recordName: "",
        message: "",
    });

    // New states for duplicate validation and save confirmation
    const [duplicateErrors, setDuplicateErrors] = useState({});
    const [saveWithErrorsModal, setSaveWithErrorsModal] = useState(false);
    
    // New state for ID field validation
    const [idFieldError, setIdFieldError] = useState("");

    const add_row = () => {
        const newId = rows.length ? Math.max(...rows.map(r => r.id)) + 1 : 1;
        set_rows(prev => [...prev, { id: newId, platform: '', product_id: '', sku_id: '', platform_open: false }]);
        setIsDirty(true);
    };
    
    const delete_row = id => {
        set_rows(prev => prev.filter(r => r.id !== id));
        // Clear duplicate error for deleted row
        setDuplicateErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[id];
            return newErrors;
        });
        setIsDirty(true);
    };
    
    const update_row = (id, changes) => {
        set_rows(prev => {
            const updated = prev.map(r => r.id === id ? { ...r, ...changes } : r);
            
            // Validate duplicates after update
            const errors = {};
            updated.forEach((row, index) => {
                if (row.platform && (row.product_id || row.sku_id)) {
                    updated.forEach((prevRow, prevIndex) => {
                        if (prevIndex !== index && 
                            prevRow.platform === row.platform && 
                            (prevRow.product_id === row.product_id || prevRow.sku_id === row.sku_id)) {
                            errors[row.id] = "ID already used on this platform";
                        }
                    });
                }
            });
            setDuplicateErrors(errors);
            
            return updated;
        });
        setRowsUpdateCheck(rowsUpdateCheck + 1);
        setIsDirty(true);
    };

    const save_ref2 = useRef(null);
    const [save_modal_open_2, set_save_modal_open_2] = useState(false);
    const handle_save_confirm_2 = () => set_save_modal_open_2(false);
    useEffect(() => {
        function outside(e) { if (save_ref2.current && !save_ref2.current.contains(e.target)) set_save_modal_open_2(false); }
        if (save_modal_open_2) document.addEventListener('mousedown', outside);
        return () => document.removeEventListener('mousedown', outside);
    }, [save_modal_open_2]);

    const select_stock_ref = useRef(null);
    const [select_stock_open, set_select_stock_open] = useState(false);
    const [platformOptions, setPlatformOptions] = useState([]);
    const [select_stock_search, set_select_stock_search] = useState("");
    const [form_data, set_form_data] = useState({
        select_stock: "",
        id: "",
        status: "1",
        notes: "",
    });

    const toggle_select_stock = () => set_select_stock_open((s) => !s);

    useEffect(() => {
        async function fetchStockInstances() {
            setLoading(true);
            setError(null);
            try {
                const response = await apiHelper(
                    "inventory/stocks/?archived=false&active=true",
                    "GET"
                );

                const apiData = await response.json();
                console.log("API Response:", apiData);

                if (!Array.isArray(apiData)) {
                    setStockOptions([]);
                    return;
                }

                const mapped = apiData.map((item) => ({
                    id: item.stock_id,
                    title: item.spu_name,               // Product name
                    sku: item.sku_name,                 // Variant
                    sku_code: item.sku_code,
                    openId: item.sku_uuid,
                    condition: item.condition || "NA",
                    status: item.status ? "Active" : "Inactive",
                    is_archived: item.is_archived,
                    is_listed: item.is_listed || false,
                }));

                setStockOptions(mapped);
            } catch (err) {
                console.error(err);
                setError("Failed to load stock");
            } finally {
                setLoading(false);
            }
        }

        fetchStockInstances();
    }, []);

    useEffect(() => {
        async function fetchPlatforms() {
            setLoading(true);
            setError(null);

            try {
                const response = await apiHelper(
                    "sales/platforms/",
                    "GET"
                );

                const apiData = await response.json();
                console.log("Platforms API Response:", apiData);

                if (!Array.isArray(apiData)) {
                    setPlatformOptions([]);
                    return;
                }

                const mapped = apiData.map(item => item.platform_name);
                setPlatformOptions(mapped);

            } catch (err) {
                console.error(err);
                setError("Failed to load platforms");
            } finally {
                setLoading(false);
            }
        }

        fetchPlatforms();
    }, []);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                select_stock_open &&
                select_stock_ref.current &&
                !select_stock_ref.current.contains(e.target)
            ) {
                set_select_stock_open(false);
                handleStockBlur();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [select_stock_open, form_data.select_stock, duplicate_stock_error]);

    const handle_select_stock_select = (opt) => {
        set_form_data((prev) => ({ ...prev, select_stock: opt }));
        set_select_stock_open(false);
        set_select_stock_search("");
        set_form_data((prev) => ({
            ...prev,
            select_stock: opt,
        }));
        setSelectStockError("");
        setIsDirty(true);
    };

    const handleStockBlur = () => {
        if (!form_data.select_stock) {
            setSelectStockError("Missing required information");
            return;
        }

        if (form_data.select_stock?.is_listed) {
            setSelectStockError("This Stock is already listed. Please select another");
            return;
        }

        setSelectStockError("");
    };

    useEffect(() => {
        console.log('location.state on mount:', location.state);

        if (location && location.state && location.state.success) {
            setSuccessMessage(location.state.success);
            history.replace(location.pathname, {});

            const t = setTimeout(() => setSuccessMessage(''), 5000);
            return () => clearTimeout(t);
        }
    }, [location, history]);

    useEffect(() => {
        if (successMessage) {
            const t = setTimeout(() => setSuccessMessage(""), 5000);
            return () => clearTimeout(t);
        }
    }, [successMessage]);

    // Check if Test Sync button should be enabled - UPDATED to check for errors
    useEffect(() => {
        const hasValidRows = rows.some(r => r.platform && r.product_id && r.sku_id);
        const hasNoRowErrors = !rows.some(r => r.hasError || duplicateErrors[r.id]);
        setEnableTestSync(hasValidRows && hasNoRowErrors);
    }, [rows, rowsUpdateCheck, duplicateErrors]);

    // --- Utilities ---
    const escapeRegExp = (string) => String(string).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

    const normalizeForSearch = (s) =>
        String(s || "")
            .replace(/[\u2013\u2014–—\-|\/\\(),:;]+/g, " ")
            .replace(/\s+/g, " ")
            .trim()
            .toLowerCase();

    const buildTokens = (query) => normalizeForSearch(query).split(" ").filter(Boolean);

    const highlightForTokens = (text, tokens) => {
        if (!tokens || tokens.length === 0) return text;

        const uniqueTokens = Array.from(new Set(tokens)).sort((a, b) => b.length - a.length);
        const safePattern = uniqueTokens.map((t) => escapeRegExp(t)).join("|");
        const regex = new RegExp(`(${safePattern})`, "ig");

        return String(text).split(regex).map((part, i) => {
            if (!part) return null;
            if (regex.test(part)) {
                return (
                    <span key={i} style={{ color: "#0D6EFD" }}>
                        {part}
                    </span>
                );
            }
            return part;
        });
    };

    const tokens = buildTokens(select_stock_search);

    const filteredOptions = stockOptions.filter((opt) => {
        const haystack = normalizeForSearch(
            `${opt.title} ${opt.sku} ${opt.condition} ${opt.status}`
        );

        return tokens.length
            ? tokens.every((t) => haystack.includes(t))
            : true;
    });

    const showFinalBg = filteredOptions.length === 1 && tokens.length > 0;

    // Close any open platform portals when clicking outside
    useEffect(() => {
        const handler = (e) => {
            const clickedInPortal = portalRoot && portalRoot.contains(e.target);
            set_rows(prev => prev.map(row => {
                const el = row_refs.current[row.id]?.current;
                if (!clickedInPortal && el && !el.contains(e.target)) {
                    return { ...row, platform_open: false };
                }
                return row;
            }));
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // Check if there are any errors in rows
    const hasRowErrors = () => {
        return rows.some(row => row.hasError || duplicateErrors[row.id]);
    };
    
    // Check if there are any errors in Stock Info section
    const hasStockInfoErrors = () => {
        return selectStockError !== "" || idFieldError !== "";
    };

    const handle_save = async () => {
        // First check for Stock Info errors
        if (hasStockInfoErrors()) {
            setAlert({
                type: "error",
                action: "create",
                recordName: "Listing",
                message: "Error Saving"
            });
            setShowItemAlert(true);
            return;
        }

        let hasError = false;

        // 🔴 Inline validation
        if (!form_data.select_stock) {
            setSelectStockError("Missing required information");
            hasError = true;
        } else {
            setSelectStockError("");
        }

        // Validate if stock is already listed
        if (form_data.select_stock?.is_listed) {
            setSelectStockError("This Stock is already listed. Please select another");
            hasError = true;
        }

        // Check for row errors
        const hasErrors = hasRowErrors();
        
        // If there are errors, show confirmation modal
        if (hasErrors) {
            setSaveWithErrorsModal(true);
            return;
        }

        // 🔔 Show alert for other validation errors
        if (hasError) {
            setAlert({
                type: "error",
                action: "create",
                recordName: "Listing",
                message: "Please fix validation errors before saving."
            });
            setShowItemAlert(true);
            return;
        }

        // If no errors, proceed with save
        await saveListing(false); // false = no errors
    };

    // Function to handle saving (refactored from original handle_save)
    const saveListing = async (hasErrors = false) => {
        try {
            // Prepare platform mappings
            const platformMappings = rows
                .filter(row => row.platform && row.product_id && row.sku_id)
                .map((row, index) => {
                    // Parse price safely
                    let platformPrice = null;
                    if (row.product_price && row.product_price !== '-' && row.product_price !== 'Invalid ID') {
                        try {
                            const priceString = row.product_price.replace(/[^\d.-]/g, '');
                            const parsed = parseFloat(priceString);
                            platformPrice = isNaN(parsed) ? null : parsed;
                        } catch (error) {
                            console.error('Error parsing price:', error);
                            platformPrice = null;
                        }
                    }

                    // Parse stock quantity safely
                    let platformStockQuantity = 0;
                    if (row.prioduct_stock && row.prioduct_stock !== '-' && row.prioduct_stock !== 'Invalid ID') {
                        try {
                            const stockString = row.prioduct_stock.replace(/[^\d]/g, '');
                            const parsed = parseInt(stockString, 10);
                            platformStockQuantity = isNaN(parsed) ? 0 : parsed;
                        } catch (error) {
                            console.error('Error parsing stock:', error);
                            platformStockQuantity = 0;
                        }
                    }

                    return {
                        platform: row.platform,
                        product_id: row.product_id,
                        sku_id: row.sku_id,
                        platform_product_name: row.product_name || '',
                        platform_sku_name: row.sku_name || '',
                        platform_price: platformPrice,
                        platform_stock_quantity: platformStockQuantity
                    };
                });

            // Single API call payload
            const payload = {
                stock_id: form_data.select_stock.id,
                listing_code: form_data.id || null,
                status: form_data.status === "1",
                notes: form_data.notes || '',
                platform_mappings: platformMappings
            };

            console.log("Creating listing with platform mappings:", payload);

            // Use the new single API endpoint
            const response = await apiHelper(
                "sales/listings/create-with-mappings/",  // New endpoint
                "POST",
                payload
            );

            const result = await response.json();
            console.log("API Response:", result);

            if (!response.ok) {
                setAlert({
                    type: "error",
                    action: "create",
                    recordName: "Listing",
                    message: result.error || "Failed to create listing"
                });
                setShowItemAlert(true);
                return;
            }

            // Success - clear dirty state and redirect with appropriate message
            setIsDirty(false);
            
            if (hasErrors) {
                history.push("/listing-pricing", {
                    success: "Listing saved with errors",
                    type: "warning"
                });
            } else {
                history.push("/listing-pricing", {
                    success: "Listing Saved Successfully",
                    type: "success"
                });
            }

        } catch (error) {
            console.error("Error in saveListing:", error);
            setAlert({
                type: "error",
                action: "create",
                recordName: "Listing",
                message: error.message || "An unexpected error occurred while saving"
            });
            setShowItemAlert(true);
        }
    };

    const handleClearSelectStock = (e) => {
        e.stopPropagation(); // prevent dropdown toggle
        set_form_data((prev) => ({
            ...prev,
            select_stock: null,
        }));
        setIsDirty(true);
        setSelectStockError("");
    };
    
    // Validate ID field
    const validateIdField = () => {
        if (form_data.id && form_data.id.length > 50) {
            setIdFieldError("ID cannot exceed 50 characters");
            return false;
        }
        setIdFieldError("");
        return true;
    };
    
    // Handle ID field change
    const handleIdChange = (e) => {
        const value = e.target.value;
        set_form_data({ ...form_data, id: value });
        setIsDirty(true);
        
        // Validate on change if there's an error
        if (idFieldError) {
            validateIdField();
        }
    };
    
    // Handle ID field blur
    const handleIdBlur = () => {
        validateIdField();
    };

    return (
        <div className="company-body main-company-body-inner new-look">
            <div className="breadcrumb">
                {breadcrumbs.map((crumb, index) => {
                    const isLast = index === breadcrumbs.length - 1;
                    return (
                        <span
                            key={index}
                            className={`breadcrumb-item ${crumb === "Listing & Pricing" ? "clickable" : ""}`}
                            onClick={() => { if (crumb === "Listing & Pricing") history.push('/listing-pricing'); }}
                            style={{ cursor: crumb === "Listing & Pricing" ? 'pointer' : 'default' }}
                        >
                            {crumb}{!isLast ? '  ' : ''}
                        </span>
                    );
                })}
            </div>
            <UnsavedChangesPrompt
                isDirty={isDirty}
                entityName="Listing"
            />

            {showItemAlert && alert && (
                <AlertNotification
                    type={alert.type}
                    action={alert.action}
                    recordName={alert.recordName}
                    message={alert.message}
                    onClose={() => setShowItemAlert(false)}
                />
            )}

            {successMessage && (
                <AlertNotification
                    type={location.state?.type || "success"}
                    action="saved"
                    recordName="Listing"
                    message={successMessage}
                    onClose={() => setSuccessMessage('')}
                />
            )}

            <div className='edit-organisation-info'>
                <div className='edit-organisation-left'>
                    <h1>Add Listing <Tooltip content="Add Listing" title="Add Listing" body="Add Listing"><Info size={16} className="info-icon" /></Tooltip></h1>
                </div>
                <div className='discard-save-organisation-right d-flex align-items-center justify-content-end flex-wrap gap-2'>
                    <button className="dark-button" type="button" onClick={() => history.push("/listing-pricing")}> Cancel </button>

                    <TestSyncModal
                        isOpen={showTestSyncModal}
                        onClose={() => setShowTestSyncModal(false)}
                        onConfirm={() => setShowTestSyncModal(false)}
                        rows={rows}
                        setRows={set_rows}
                        selectedStock={form_data.select_stock} 
                    />
                    <button className="dark-border" type="button" onClick={handle_save}>Save & Exit</button>
                </div>
            </div>

            <div className="employee-tabs-container">
                {save_modal_open_2 && (
                    <>
                        <div className="drawer-overlay" />
                        <div className="entity-drawer modal-popup-cls" ref={save_ref2} style={{ maxWidth: 650, margin: '0 auto', background: 'white', borderRadius: 12, zIndex: 1050, padding: 24, position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', boxShadow: '0px 2px 10px rgba(0,0,0,0.2)', height: 'auto', right: 'auto', overflow: 'inherit' }}>
                            <div className="drawer-header d-flex justify-content-end align-items-center pt-0 pe-0">
                                <button className="close-btn" onClick={() => set_save_modal_open_2(false)}><X size={20} /></button>
                            </div>
                            <h3 style={{ textAlign: 'left', color: '#212529', fontSize: '20px', fontWeight: '600', lineHeight: '24px', fontFamily: 'Montserrat', marginBottom: '5px' }}>The edit function implementation</h3>
                            <div className="drawer-footer" style={{ paddingTop: '10px' }}>
                                <button className="btn-save" onClick={handle_save_confirm_2}>OK</button>
                            </div>
                        </div>
                    </>
                )}

                {/* Save with errors confirmation modal */}
                {saveWithErrorsModal && (
                    <>
                        <div className="drawer-overlay" />
                        <div className="entity-drawer modal-popup-cls" style={{ 
                            minWidth: "650px", 
                            margin: '0 auto', 
                            background: 'white', 
                            borderRadius: 12, 
                            zIndex: 1050, 
                            padding: 24, 
                            position: 'fixed', 
                            top: '50%', 
                            left: '50%', 
                            transform: 'translate(-50%, -50%)', 
                            boxShadow: '0px 2px 10px rgba(0,0,0,0.2)', 
                            height: 'auto', 
                            right: 'auto', 
                            overflow: 'inherit' 
                        }}>
                            <div className="drawer-header d-flex justify-content-end align-items-center pt-0 pe-0">
                                <button className="close-btn" onClick={() => setSaveWithErrorsModal(false)}>
                                    <X size={20} />
                                </button>
                            </div>
                            <h3 style={{ 
                                textAlign: 'left', 
                                color: '#212529', 
                                fontSize: '20px', 
                                fontWeight: '600', 
                                lineHeight: '24px', 
                                fontFamily: 'Montserrat', 
                                marginBottom: '15px' 
                            }}>
                                Save Listing With Errors?
                            </h3>
                            <p style={{ 
                                color:  "#6C757D",
                                fontSize: "14px",
                                fontWeight: 400,
                                lineHeight: "18px", 
                            }}>
                                Listings with invalid Product ID or SKU ID can’t be mapped, so actions that require syncing to e-commerce platforms such as pricing updates won’t work
                            </p>
                            <div className="drawer-footer" style={{ 
                                paddingTop: '10px', 
                                display: 'flex', 
                                justifyContent: 'flex-end', 
                                gap: '10px' 
                            }}>
                                <button 
                                    className="dark-button" 
                                    onClick={() => setSaveWithErrorsModal(false)}
                                    style={{
              display: "flex",
              height: "34px",
              minWidth: "100px",
              padding: "10px",
              justifyContent: "center",
              alignItems: "center",
              gap: "10px",
              borderRadius: "8px",
              border: "1px solid  var(--navbar-dropdown-active, #212529)",
              background: "var(--Background-White, #FFF)",
              boxShadow: "0 1px 4px 0 rgba(0, 0, 0, 0.10)",
              color: "var(--navbar-dropdown-active, #212529)",
              fontFamily: "Montserrat",
              fontSize: "12px",
              fontWeight: 600,
              fontStyle: "normal",
              lineHeight: "normal",
            }}
                                >
                                    Cancel
                                </button>
                                <button 
                                    className="btn-save" 
                                    onClick={async () => {
                                        setSaveWithErrorsModal(false);
                                        await saveListing(true); // true = has errors
                                    }}
                                    style={{
              display: "flex",
              height: "34px",
              minWidth: "100px",
              padding: "10px",
              justifyContent: "center",
              alignItems: "center",
              gap: "10px",
              borderRadius: "8px",
              background: "var(--navbar-dropdown-active, #212529)",
              boxShadow: "0 1px 4px 0 rgba(0, 0, 0, 0.10)",
              color: "var(--Background-White, #FFF)",
              fontFamily: "Montserrat",
              fontSize: "12px",
              fontWeight: 600,
              fontStyle: "normal",
              lineHeight: "normal",
            }}
                                >
                                    Save
                                </button>
                            </div>
                        </div>
                    </>
                )}

                <div className="tab-content" id="edit-spu-content">
                    <div className="info-section">
                        {/* SPU / Stock INFO CARD */}
                        <div className="info-card new-info-overflow-auto">
                            <div className="info-header"><h4 className="info-title">Stock Info</h4></div>
                            <div className="info-content d-block">
                                <div className='grid-info-adding'>

                                    {/* ROW: Select Stock & Status */}
                                    <div className='row gappingcls-added'>
                                        <div className="col-lg-6 col-md-6 col-12">
                                            <div className="info-inner">
                                                <label htmlFor="select_stock" className="form-label d-flex align-items-center gap-1">
                                                    Select Stock<span className="error-color">*</span>
                                                    <Tooltip title="Select Stock" body="Select a Stock for Listing and Platform Mapping"><Info size={16} className="info-icon" /></Tooltip>
                                                </label>

                                                <div style={{ position: "relative" }} ref={select_stock_ref}>
                                                    <div
                                                        className={`form-control common-input-all d-flex justify-between items-center cursor-pointer rounded p-2 ${selectStockError ? 'err-color' : ''}`}
                                                        onClick={toggle_select_stock}
                                                        style={{ display: "flex", justifyContent: "space-between", alignItems: "center", minHeight: "40px" }}
                                                    >
                                                        {form_data.select_stock ? (
                                                            <div style={{ display: "flex", flexDirection: "column" }}>
                                                                <span style={{ fontSize: "14px", color: "#212529" }}>{form_data.select_stock.title}</span>

                                                                {/* SKU row (single line) */}
                                                                <div style={{ fontSize: "12px", color: "#6C757D", display: "flex", alignItems: "center", whiteSpace: "nowrap" }}>
                                                                    <span style={{ display: "flex", alignItems: "center", gap: "4px", lineHeight: "normal" }}>
                                                                        <span>SKU:</span>{" "}
                                                                        <span>{form_data.select_stock.sku}</span>
                                                                    </span>

                                                                    <span style={{ margin: "0 6px" }}>|</span>

                                                                    <span
                                                                        style={{
                                                                            background: "#FFEB00",
                                                                            borderRadius: "100px",
                                                                            fontSize: "10px",
                                                                            fontWeight: 600,
                                                                            color: "#000000",
                                                                            padding: "2px 5px",
                                                                            marginLeft: "4px",
                                                                            marginRight: "6px",
                                                                        }}
                                                                    >
                                                                        {form_data.select_stock.condition}
                                                                    </span>
                                                                    <span style={{
                                                                        background: "#FFEB00",
                                                                        borderRadius: "100px",
                                                                        fontSize: "10px",
                                                                        fontWeight: 600,
                                                                        color: "#000000",
                                                                        padding: "2px 5px",
                                                                    }}>{form_data.select_stock.status}</span>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            "Search and Select"
                                                        )}
                                                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                                            {form_data.select_stock && (
                                                                <span
                                                                    onClick={handleClearSelectStock}
                                                                    style={{
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        cursor: "pointer",
                                                                    }}
                                                                >
                                                                    <svg
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                        width="12"
                                                                        height="12"
                                                                        viewBox="0 0 12 12"
                                                                        fill="none"
                                                                    >
                                                                        <path
                                                                            d="M9 3L3 9M3 3L9 9"
                                                                            stroke="#212529"
                                                                            strokeWidth="1.33333"
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                        />
                                                                    </svg>
                                                                </span>
                                                            )}

                                                            <ChevronDown size={16} />
                                                        </div>
                                                    </div>

                                                    {select_stock_open && (
                                                        <div
                                                            className="z-10 w-full bg-white border rounded shadow-lg mt-1"
                                                            style={{ maxHeight: "320px", overflowY: "auto", position: "absolute", right: 0, left: 0, width: "100%", zIndex: 1 }}
                                                        >
                                                            {/* Search box */}
                                                            <div
                                                                className="search-box"
                                                                style={{
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    minWidth: "260px",
                                                                    border: "1px solid rgb(108, 117, 125)",
                                                                    borderRadius: "8px",
                                                                    background: "#fff",
                                                                    position: "relative",
                                                                    margin: "0.5rem",
                                                                }}
                                                            >
                                                                <div style={{ position: "absolute", left: "10px", display: "flex", alignItems: "center", height: "100%" }}>
                                                                    <Search size={20} />
                                                                </div>
                                                                <input
                                                                    type="text"
                                                                    autoFocus
                                                                    style={{ width: "100%", padding: "10px 10px 10px 40px", border: "none", outline: "none", background: "transparent", fontSize: "14px" }}
                                                                    placeholder="Search stock..."
                                                                    value={select_stock_search}
                                                                    onChange={(e) => set_select_stock_search(e.target.value)}
                                                                />
                                                            </div>

                                                            {/* List items */}
                                                            {filteredOptions.map((opt, index) => {
                                                                const hay = normalizeForSearch(`${opt.title} ${opt.sku} ${opt.condition} ${opt.status}`);
                                                                const matches = tokens.length ? tokens.every((tok) => hay.includes(tok)) : true;
                                                                const bg = showFinalBg && matches ? "#FFFEF0" : "white";

                                                                return (
                                                                    <div
                                                                        key={index}
                                                                        className="p-2 flex flex-col cursor-pointer"
                                                                        onClick={() => handle_select_stock_select(opt)}
                                                                        style={{ background: bg }}
                                                                    >
                                                                        {/* Title */}
                                                                        <span style={{ fontSize: "14px", color: "#212529", display: "block" }}>
                                                                            {highlightForTokens(opt.title, tokens)}
                                                                        </span>

                                                                        {/* SKU row: kept inline (no wrap) and highlight only for SKU text */}
                                                                        <div style={{ fontSize: "12px", color: "#6C757D", display: "flex", alignItems: "center", whiteSpace: "nowrap" }}>
                                                                            <span style={{ display: "flex", alignItems: "center", gap: "4px", lineHeight: "normal" }}>
                                                                                <span>SKU:</span>{" "}
                                                                                <span>{highlightForTokens(opt.sku, tokens)}</span>
                                                                            </span>

                                                                            <span style={{ margin: "0 6px" }}>|</span>

                                                                            {/* condition pill - NO blue highlighting here */}
                                                                            <span
                                                                                style={{
                                                                                    background: "#FFEB00",
                                                                                    borderRadius: "100px",
                                                                                    fontSize: "10px",
                                                                                    fontWeight: 600,
                                                                                    color: "#000000",
                                                                                    padding: "2px 5px",
                                                                                    marginLeft: "4px",
                                                                                    marginRight: "6px",
                                                                                }}
                                                                            >
                                                                                {opt.condition}
                                                                            </span>

                                                                            <span style={{
                                                                                background: "#FFEB00",
                                                                                borderRadius: "100px",
                                                                                fontSize: "10px",
                                                                                fontWeight: 600,
                                                                                color: "#000000",
                                                                                padding: "2px 5px",
                                                                            }}>{opt.status}</span>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}

                                                            {filteredOptions.length === 0 && (
                                                                <div
                                                                    style={{
                                                                        padding: "24px 12px",
                                                                        textAlign: "center",
                                                                        color: "#6C757D",
                                                                        fontSize: 14,
                                                                    }}
                                                                >
                                                                    <img
                                                                        src={EmpltyBoxIcon}
                                                                        alt="No results"
                                                                        style={{
                                                                            width: 48,
                                                                            height: 48,
                                                                            marginBottom: 8,
                                                                            opacity: 0.6,
                                                                        }}
                                                                    />

                                                                    <div>No Results</div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                                {selectStockError && (
                                                    <div
                                                        className="invalid-feedback d-block"
                                                        style={{ marginTop: "4px" }}
                                                    >
                                                        {selectStockError}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className='col-lg-6 col-md-6 col-12'>
                                            <div className="info-inner">
                                                <label htmlFor="status_active" className="form-label">Status <span className='error-color'>*</span></label>
                                                <div className='flexing-type-radio'>
                                                    <div className="form-check">
                                                        <input 
                                                            className="form-check-input" 
                                                            type="radio" 
                                                            id="status_active" 
                                                            name="status" 
                                                            value="1" 
                                                            checked={form_data.status === '1'} 
                                                            onChange={(e) => {
                                                                set_form_data({ ...form_data, status: e.target.value });
                                                                setIsDirty(true);
                                                            }} 
                                                        />
                                                        <label className="form-check-label" htmlFor="status_active" style={{ fontWeight: 'normal', fontSize: '14px' }}>Active</label>
                                                    </div>
                                                    <div className="form-check">
                                                        <input 
                                                            className="form-check-input" 
                                                            type="radio" 
                                                            id="status_inactive" 
                                                            name="status" 
                                                            value="0" 
                                                            checked={form_data.status === '0'} 
                                                            onChange={(e) => {
                                                                set_form_data({ ...form_data, status: e.target.value });
                                                                setIsDirty(true);
                                                            }} 
                                                        />
                                                        <label className="form-check-label" htmlFor="status_inactive" style={{ fontWeight: 'normal', fontSize: '14px' }}>Inactive</label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                    </div>

                                    {/* ROW: ID Field */}
                                    <div className='row gappingcls-added'>
                                        <div className='col-lg-6 col-md-6 col-12'>
                                            <div className="info-inner">
                                                <label htmlFor="id" className="form-label d-flex align-items-center gap-1">ID
                                                </label>
                                                <CharacterLimitedTextarea
                                                    id="id"
                                                    name="id"
                                                    value={form_data.id}
                                                    onChange={handleIdChange}
                                                    onBlur={handleIdBlur}
                                                    maxLength={50}
                                                    counterPosition="end"
                                                    className={idFieldError ? 'err-color' : ''}
                                                />
                                                {idFieldError && (
                                                    <div
                                                        className="invalid-feedback d-block"
                                                        style={{ marginTop: "4px" }}
                                                    >
                                                        {idFieldError}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                    </div>

                                </div>
                            </div>
                        </div>

                        {/* Platform mapping card (portal dropdown for platform selection) */}
                        <div className="info-card new-info-overflow-auto main-company-body-inner">
                            <div className="info-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                <h4 className="info-title">Platform Mapping</h4>
                                <button 
                                    className={!enableTestSync ? 'disabled' : ''} 
                                    style={{ 
                                        borderRadius: '8px', 
                                        background: '#ffffff', 
                                        padding: '10px', 
                                        fontSize: '12px', 
                                        border: "1px solid #212529", 
                                        height: "34px", 
                                        minWidth: "100px", 
                                        display: "flex", 
                                        justifyContent: "center", 
                                        alignItems: "center", 
                                        gap: '10px', 
                                        fontFamily: "Montserrat", 
                                        fontSize: '12px', 
                                        fontWeight: "600", 
                                        fontStyle: "normal", 
                                        lineHeight: "normal",
                                        cursor: enableTestSync ? 'pointer' : 'not-allowed',
                                        opacity: enableTestSync ? 1 : 0.5
                                    }} 
                                    onClick={() => {
                                        if (enableTestSync) {
                                            setShowTestSyncModal(true);
                                        }
                                    }}
                                >
                                    Test Sync
                                </button>
                            </div>
                            <div className="info-content d-block entities-tab">
                                <div style={{ padding: '20px', boxShadow: '0px 1px 4px #0000001A', borderRadius: '12px' }}>
                                    <div className="table-wrapper" style={{ position: 'relative', overflowX: 'auto' }}>
                                        <table className="entities-table tab-table w-100">
                                            <thead height="35px">
                                                <tr>
                                                    <th />
                                                    <th><span>Platform <span style={{ color: "red" }}>*</span> </span></th>
                                                    <th><span>Product ID  <span style={{ color: "red" }}>*</span></span></th>
                                                    <th><span>SKU ID  <span style={{ color: "red" }}>*</span></span></th>
                                                    <th /></tr>
                                            </thead>
                                            <ReactSortable list={rows} setList={set_rows} handle=".drag-handle" tag="tbody">
                                                {rows.map((r, idx) => {
                                                    if (!row_refs.current[r.id]) row_refs.current[r.id] = React.createRef();
                                                    return (
                                                        <PlatformRow key={r.id}
                                                            ref_node={row_refs.current[r.id]}
                                                            row_state={r}
                                                            delete_row={delete_row}
                                                            update_row={update_row}
                                                            platform_options={platformOptions}
                                                            SuccessIcon={SuccessIcon}
                                                            FailedIcon={FailedIcon}
                                                            duplicateErrors={duplicateErrors}
                                                        />
                                                    );
                                                })}
                                            </ReactSortable>
                                        </table>
                                    </div>

                                    <div className="edit-organisation-info" style={{ display: 'flex', marginTop: '20px', padding: '10px' }}>
                                        <button onClick={add_row} className="dark-button">Add Mapping</button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Additional Info */}
                        <div className="info-card">
                            <div className="info-header"><h4 className="info-title">Additional Info</h4></div>
                            <div className="info-content">
                                <div className="info-inner common-textarea">
                                    <label htmlFor="notes" className="form-label">Note</label>
                                    <textarea 
                                        className="form-control w-100 common-textarea-all" 
                                        id="notes" 
                                        rows="3" 
                                        name="notes" 
                                        value={form_data.notes} 
                                        onChange={(e) => {
                                            set_form_data({ ...form_data, notes: e.target.value });
                                            setIsDirty(true);
                                        }}
                                    ></textarea>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
}

// PlatformRow component - portal dropdown for platform selection and naming follow Figma labels
function PlatformRow({ ref_node, row_state: r, delete_row, update_row, platform_options, SuccessIcon, FailedIcon, duplicateErrors }) {
    const platform_button_ref = useRef(null);
    const [portal_style, set_portal_style] = useState({});

    useEffect(() => {
        if (r.platform_open && platform_button_ref.current) {
            const rect = platform_button_ref.current.getBoundingClientRect();
            set_portal_style({
                position: 'absolute',
                top: rect.bottom + window.scrollY + 8,
                left: rect.left + window.scrollX,
                width: Math.max(280, rect.width),
                maxHeight: 270,
                overflowY: 'auto',
                zIndex: 2000,
            });
        }
    }, [r.platform_open, r.platform]);

    // Check if this row has any error (API error or duplicate error)
    const hasError = r.hasError || duplicateErrors?.[r.id];
    const isDuplicateError = duplicateErrors?.[r.id] && !r.hasError;
    const isApiError = r.hasError && !duplicateErrors?.[r.id];

    return (
        <tr ref={ref_node} style={{ position: (r.platform_open) ? 'relative' : 'static', zIndex: (r.platform_open) ? 1000 : 'auto' }}>
            <td style={{ width: '60px' }}>
                <button className="btn p-0" onClick={() => delete_row(r.id)} style={{ boxShadow: 'none' }}>
                    <Trash2 size={18} />
                </button>
            </td>

            <td style={{ position: 'relative', minWidth: '200px', maxWidth: '200px' }}>
                <div ref={platform_button_ref} className="form-control new-form-select-cls"
                    onClick={() => update_row(r.id, { platform_open: !r.platform_open })}
                    style={{
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0.5rem',
                        minHeight: '40px',
                        background: 'none',
                        // Only show red border for API errors, not for duplicate errors
                        borderColor: isApiError ? '#B02A37' : '#CED4DA'
                    }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginRight: 'auto' }}>
                        {/* Show success/error icon in platform name field */}
                        {r.verified && !hasError && <SuccessIcon />}
                        {hasError && <FailedIcon />}
                        {r.platform || 'Select platform'}
                    </span>
                    <ChevronDown size={16} style={{ transform: r.platform_open ? 'rotate(180deg)' : 'rotate(0)' }} />
                </div>

                {/* NO duplicate error message below platform field - moved to Product ID and SKU ID fields */}
                {r.platform_open && (
                    <OptionPortal style={portal_style}>
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                            {platform_options.map((opt, i) => (
                                <li key={i} onClick={() => update_row(r.id, { platform: opt, platform_open: false })} style={{ padding: '10px', cursor: 'pointer', borderRadius: 6, fontFamily: 'Montserrat', color: '#212529' }}>{opt}</li>
                            ))}
                        </ul>
                    </OptionPortal>
                )}
            </td>

            <td style={{ minWidth: '200px', maxWidth: '200px', position: 'relative' }}>
                <input
                    id={`product_id_${r.id}`}
                    name={`product_id_${r.id}`}
                    className="form-control common-input-all"
                    value={r.product_id}
                    onChange={e => update_row(r.id, { product_id: e.target.value })}
                    style={{
                        // Red border for both API errors and duplicate errors
                        border: hasError ? '1px solid #B02A37' : '1px solid #CED4DA'
                    }}
                />
                {/* Show inline error message below Product ID field */}
                {isApiError && (
                    <div style={{
                        color: '#B02A37',
                        fontSize: '12px',
                        marginTop: '4px',
                        paddingLeft: '4px'
                    }}>
                        Invalid ID
                    </div>
                )}
                {isDuplicateError && (
                    <div style={{
                        color: '#B02A37',
                        fontSize: '12px',
                        marginTop: '4px',
                        paddingLeft: '4px'
                    }}>
                        ID already used on this platform
                    </div>
                )}
            </td>

            <td style={{ minWidth: '200px', maxWidth: '200px', position: 'relative' }}>
                <input
                    id={`sku_id_${r.id}`}
                    name={`sku_id_${r.id}`}
                    className="form-control common-input-all"
                    value={r.sku_id}
                    onChange={e => update_row(r.id, { sku_id: e.target.value })}
                    style={{
                        // Red border for both API errors and duplicate errors
                        border: hasError ? '1px solid #B02A37' : '1px solid #CED4DA'
                    }}
                />
                {/* Show inline error message below SKU ID field */}
                {isApiError && (
                    <div style={{
                        color: '#B02A37',
                        fontSize: '12px',
                        marginTop: '4px',
                        paddingLeft: '4px'
                    }}>
                        Invalid ID
                    </div>
                )}
                {isDuplicateError && (
                    <div style={{
                        color: '#B02A37',
                        fontSize: '12px',
                        marginTop: '4px',
                        paddingLeft: '4px'
                    }}>
                        ID already used on this platform
                    </div>
                )}
            </td>

            <td className="drag-handle" style={{ cursor: 'grab' }}><Menu size={18} /></td>
        </tr>
    );
}